import { getRequestGeneric, postRequestGeneric } from "@/services/backendApiGeneric";

/**
 * Per-location KI-Seiten quota snapshot. The monthly allowance
 * (`pagesIncluded`, default 150) resets at the start of each calendar
 * month; `pagesTopUp` is a persistent balance that accumulates from
 * one-time bundle purchases (Stripe Checkout, 100 pages / 9,99 €).
 *
 * `pagesAvailable = pagesRemaining + pagesTopUp` is the number the UI
 * must show as "verfügbar" — an upload of more pages than this is
 * rejected by the backend with HTTP 402.
 */
export interface AIPageLocationQuota {
  idLocation: number;
  locationName: string;
  yearMonth: string;
  pagesIncluded: number;
  pagesUsed: number;
  pagesRemaining: number;
  pagesTopUp: number;
  pagesAvailable: number;
  usagePercent: number;
  isExhausted: boolean;
  isWarning: boolean;
}

export interface AIPageOverview {
  yearMonth: string;
  isAdmin: boolean;
  locations: AIPageLocationQuota[];
  totalIncluded: number;
  totalUsed: number;
  totalTopUp: number;
  totalAvailable: number;
  usagePercent: number;
  hasWarning: boolean;
  hasExhausted: boolean;
  bundlePages: number;
  bundleCents: number;
  bundleCurrency: string;
}

/**
 * Body returned by quota-guarded endpoints (HTTP 402) when an upload
 * would exceed the location's KI-Kontingent. Mirrors the Go struct
 * `aipages.QuotaViolation`.
 */
export interface AIPageQuotaViolation {
  code: "ai_page_quota_exceeded";
  message: string;
  idLocation: number;
  locationName?: string;
  pagesIncluded: number;
  pagesUsed: number;
  pagesTopUp: number;
  pagesAvailable: number;
  pagesRequested: number;
}

export async function getAIPageOverview(): Promise<AIPageOverview> {
  return (await getRequestGeneric("/protected/ai-pages/overview")) as AIPageOverview;
}

export async function createAIPageTopUpCheckout(
  idLocation: number
): Promise<{ url: string }> {
  return (await postRequestGeneric(
    "/protected/ai-pages/topup-checkout",
    { idLocation },
    true
  )) as { url: string };
}
